<template>
  <system-frame class="unpaid" title="Maksmata tööd" section="Andmete korrastamine" hint="Vanad lõpetatud töökäsud, mis on jäänud Maksmata: sulge soovituse järgi hulgi või märgi käsitsi.">
    <!-- Old unpaid töökäsud closed in batches (server/service/unpaidSettle.js):
         a suggestion closes its whole set; in the list below each row gets its
         makseviis picked at its end, and Salvesta saves them together. Every
         batch can be taken back. Layout agreed on the mockup first. -->
    <p class="text-medium-emphasis mb-4" style="max-width: 72ch">
      Siin on lõpetatud töökäsud, mille makseviis on Maksmata. Sulgemisel saab sissekanne makseviisi ja rea ajalukku; muutmise kuupäev jääb samaks. Iga muudatuse saab lehe lõpus tagasi võtta.
    </p>

    <div class="d-flex flex-wrap ga-8 mb-4">
      <div><div class="text-grey-darken-1 text-body-2">Maksmata töid</div><div class="text-h5">{{ summary.total }}</div></div>
      <div><div class="text-grey-darken-1 text-body-2">Kliente</div><div class="text-h5">{{ summary.clients }}</div></div>
    </div>

    <card class="mb-6">
      <div class="text-h6">Soovitused</div>
      <p class="text-body-2 text-medium-emphasis mb-3">Iga soovitus sulgeb kõik oma sissekanded korraga. Enne küsitakse üle.</p>
      <div v-for="rule in rules" :key="rule.key" class="unpaid__rule">
        <div class="unpaid__rule-text">
          <b>{{ rule.title }}</b>
          <div class="text-body-2 text-medium-emphasis">{{ rule.text }}</div>
          <date-field
            v-if="rule.key === 'before'"
            v-model="before"
            label="Vanemad kui"
            density="compact"
            variant="outlined"
            hide-details
            class="mt-2"
            style="max-width: 200px"
          />
        </div>
        <div class="unpaid__rule-do">
          <span class="unpaid__num text-medium-emphasis">{{ rule.count == null ? '—' : rule.count + (rule.count === 1 ? ' töö' : ' tööd') }}</span>
          <v-btn
            v-for="type in rule.types"
            :key="type.value"
            size="small"
            variant="tonal"
            color="primary"
            :disabled="!rule.count"
            @click="askRule(rule, type)"
          >{{ type.text }}</v-btn>
        </div>
      </div>
    </card>

    <card class="mb-6 unpaid__work">
      <div class="text-h6">Käsitsi</div>
      <p class="text-body-2 text-medium-emphasis mb-3">Kliendid, kellel on kõige rohkem maksmata töid, on eespool. Vali rea lõpus makseviis ja vajuta Salvesta. Kuupäev avab töökäsu.</p>
      <div class="d-flex flex-wrap ga-2 mb-3">
        <v-chip
          v-for="item in summary.years"
          :key="item.year"
          :color="year === item.year ? 'green' : undefined"
          :variant="year === item.year ? 'tonal' : 'outlined'"
          @click="year = year === item.year ? null : item.year"
        ><b>{{ item.year }}</b><span class="ml-1 text-medium-emphasis">{{ item.count }}</span></v-chip>
      </div>
      <div class="d-flex flex-wrap align-center ga-4 mb-3">
        <v-text-field v-model="q" label="Otsi klienti, aadressi või märkust" density="compact" hide-details clearable prepend-inner-icon="mdi-magnify" style="min-width: 220px; max-width: 360px" />
        <v-select v-model="category" :items="categories" label="Tüüp" density="compact" hide-details clearable style="min-width: 160px; max-width: 200px" />
        <v-select v-model="technicId" :items="technics" item-title="fullName" item-value="id" label="Tehnik" density="compact" hide-details clearable style="min-width: 160px; max-width: 220px" />
        <span class="text-body-2 text-medium-emphasis">{{ count }} tööd · {{ clientCount }} klienti</span>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

      <!-- Nothing is saved until Salvesta: the picked rows wait, marked. -->
      <div class="unpaid__save">
        <span class="unpaid__save-state">
          <template v-if="pendingCount"><b>{{ pendingCount }}</b> {{ pendingCount === 1 ? 'muudatus' : 'muudatust' }} ootel. Salvestamata.</template>
          <template v-else>Muudetud read märgitakse kollasega ja ootavad salvestamist.</template>
        </span>
        <v-btn variant="text" :disabled="!pendingCount || saving" @click="pending = {}">Tühista muudatused</v-btn>
        <v-btn color="green" variant="flat" :disabled="!pendingCount" :loading="saving" @click="save">{{ pendingCount ? 'Salvesta (' + pendingCount + ')' : 'Salvesta' }}</v-btn>
      </div>
      <div v-if="rows.length" class="unpaid__list">
        <div class="unpaid__head">
          <span>Kuupäev</span><span>Tüüp</span><span>Klient</span><span>Aadress ja töökäsk</span><span>Tehnik</span><span>Makseviis</span>
        </div>
        <div v-for="entry in rows" :key="entry.id" class="unpaid__row" :class="{ 'unpaid__row--changed': pending[entry.id] }">
          <button type="button" class="unpaid__day unpaid__num" title="Ava töökäsk" @click="openEvent(entry)">{{ day(entry.startDate) }}</button>
          <span class="unpaid__type">{{ entry.categoryLabel }}</span>
          <span class="unpaid__client">
            <router-link v-if="entry.clientId" :to="'/client/' + entry.clientId">{{ entry.clientName || 'Klient ' + entry.clientId }}</router-link>
            <template v-else>Kliendita</template>
          </span>
          <div class="unpaid__main">
            <router-link v-if="entry.objectId" :to="'/object/' + entry.objectId">{{ entry.address || 'Objekt ' + entry.objectId }}</router-link>
            <v-chip v-if="entry.invoiceNo" size="x-small" color="green" variant="tonal" class="ml-2">arve {{ entry.invoiceNo }}</v-chip>
            <v-chip v-else-if="entry.aktId" size="x-small" class="ml-2">akt {{ entry.aktId }}</v-chip>
            <div v-if="entry.comments" class="text-body-2 text-medium-emphasis unpaid__note">{{ entry.comments }}</div>
          </div>
          <span class="unpaid__tech text-body-2">{{ entry.technicName }}</span>
          <select
            class="unpaid__pay"
            :value="pending[entry.id] || 0"
            :aria-label="'Makseviis: ' + (entry.clientName || '') + ' ' + day(entry.startDate)"
            @change="pick(entry.id, Number($event.target.value))"
          >
            <option :value="0">Maksmata</option>
            <option v-for="type in types" :key="type.value" :value="type.value">{{ type.text }}</option>
          </select>
        </div>
      </div>
      <p v-if="loaded && !groups.length" class="text-medium-emphasis mb-0">{{ summary.total ? 'Selle filtriga pole ühtegi maksmata tööd.' : 'Maksmata töid pole.' }}</p>
      <div v-if="groups.length < clientCount" class="text-center mt-3">
        <v-btn variant="text" color="primary" :loading="loading" @click="load(true)">Näita veel ({{ clientCount - groups.length }} klienti)</v-btn>
      </div>
    </card>

    <card>
      <div class="text-h6 mb-2">Viimased muudatused</div>
      <p v-if="!log.length" class="text-medium-emphasis mb-0">Siit pole veel midagi suletud.</p>
      <v-list v-else density="compact" class="py-0">
        <v-list-item v-for="batch in log" :key="batch.id" class="px-0">
          <span class="text-medium-emphasis mr-2">{{ dateTime(batch.at) }}</span>
          <b>{{ batch.count }}</b> {{ batch.count === 1 ? 'töö' : 'tööd' }} → <b>{{ batchTypes(batch) }}</b>
          <span class="text-medium-emphasis"> · {{ ruleText(batch.rule) }}<template v-if="batch.userName"> · {{ batch.userName }}</template></span>
          <template #append>
            <span v-if="batch.undoneAt" class="text-body-2 text-medium-emphasis">tagasi võetud {{ dateTime(batch.undoneAt) }} ({{ batch.undoneCount }})</span>
            <v-btn v-else size="small" variant="text" color="primary" @click="undoing = batch">Võta tagasi</v-btn>
          </template>
        </v-list-item>
      </v-list>
    </card>

    <v-dialog :model-value="!!asking" max-width="480" @update:model-value="asking = null">
      <v-card v-if="asking">
        <v-card-title class="text-h5">Muuda makseviis</v-card-title>
        <v-card-text>
          <b>{{ asking.count }}</b> {{ asking.count === 1 ? 'töökäsk' : 'töökäsku' }} ({{ asking.what }}) saab makseviisiks <b>{{ asking.type.text }}</b>.
          <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Iga sissekanne saab rea ajalukku. Muudatuse saab lehe lõpus tagasi võtta.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="asking = null">Tühista</v-btn>
          <v-btn color="green" variant="flat" :loading="saving" @click="settle">Märgi: {{ asking.type.text }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :model-value="!!undoing" max-width="480" @update:model-value="undoing = null">
      <v-card v-if="undoing">
        <v-card-title class="text-h5">Võta muudatus tagasi?</v-card-title>
        <v-card-text>
          {{ undoing.count }} {{ undoing.count === 1 ? 'töökäsk' : 'töökäsku' }} ({{ dateTime(undoing.at) }}, {{ batchTypes(undoing) }}) läheb tagasi Maksmata.
          <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Need, mille makseviisi on vahepeal käsitsi muudetud, jäävad puutumata.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="undoing = null">Tühista</v-btn>
          <v-btn color="green" variant="flat" :loading="saving" @click="undo">Võta tagasi</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="eventOpen" :max-width="features && features.eventPlate ? 880 : 700">
      <event v-if="eventOpen" mode="view" :eventId="eventId" @close="closeEvent" @updated="eventChanged = true" @deleted="eventChanged = true" />
    </v-dialog>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import DateField from '@/components/input/DateField.vue'
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions, mapState } from 'vuex'

// Calendar.paymentTypes on the server; Maksmata (0) is what the page clears.
const TYPES = [
  { value: 1, text: 'Arvega' },
  { value: 2, text: 'Sularaha' },
  { value: 3, text: 'Tasuta' }
]
const PAGE = 25

export default {
  components: { SystemFrame, DateField },
  data () {
    return {
      summary: { total: 0, clients: 0, years: [], rules: {}, categories: {} },
      groups: [],
      count: 0,
      clientCount: 0,
      log: [],
      technics: [],
      loaded: false,
      loading: false,
      saving: false,
      q: '',
      year: null,
      category: null,
      technicId: null,
      // The fourth suggestion's date, YYYY-MM-DD.
      before: '',
      // The makseviis picked on a row and not saved yet: entry id -> type.
      pending: {},
      asking: null,
      undoing: null,
      eventOpen: false,
      eventId: null,
      eventChanged: false,
      searchTimer: null,
      // Answers to an older filter are dropped.
      request: 0
    }
  },
  computed: {
    ...mapState(['features']),
    types: () => TYPES,
    // The client groups as one list: a client's rows stay together.
    rows () {
      return this.groups.flatMap(group => group.entries.map(entry => ({ ...entry, clientId: group.clientId, clientName: group.clientName })))
    },
    pendingCount () {
      return Object.keys(this.pending).length
    },
    categories () {
      return Object.entries(this.summary.categories || {}).map(([value, title]) => ({ value: Number(value), title }))
    },
    rules () {
      const counts = this.summary.rules || {}
      const only = value => TYPES.filter(type => type.value === value)
      return [
        { key: 'guarantee', title: 'Garantii → Tasuta', what: 'Garantii', text: 'Garantiitöö eest ei maksta, nagu uutel töökäskudel.', count: counts.guarantee, types: only(3) },
        { key: 'contract', title: 'Lepinguline', what: 'Lepinguline', text: 'Lepingulised tööd katab lepingutasu. Vali, mis neist saab.', count: counts.contract, types: TYPES },
        { key: 'before', title: 'Vanemad kui kuupäev', what: this.before ? 'enne ' + this.day(this.before) : '', text: 'Kõik enne valitud päeva tehtud tööd korraga.', count: this.before ? counts.before : null, types: TYPES },
        { key: 'aktInvoice', title: 'Aktil on arve → Arvega', what: 'aktil on arve', text: 'Töö aktil on Meriti arve number või koondarve.', count: counts.aktInvoice, types: only(1) }
      ]
    }
  },
  watch: {
    year () { this.load() },
    category () { this.load() },
    technicId () { this.load() },
    q () {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.load(), 350)
    },
    before () { this.loadSummary() }
  },
  methods: {
    ...mapActions(['showNotification']),
    async loadSummary () {
      try {
        this.summary = await api.getUnpaidSummary(this.before ? { before: this.before } : {})
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    async loadLog () {
      try {
        this.log = (await api.getUnpaidLog()).log
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    // The hand-work list from the top, or its next page.
    async load (more = false) {
      const request = ++this.request
      this.loading = true
      try {
        const result = await api.getUnpaid({
          q: this.q || undefined,
          year: this.year || undefined,
          category: this.category == null ? undefined : this.category,
          technicId: this.technicId || undefined,
          limit: PAGE,
          offset: more ? this.groups.length : 0
        })
        if (request !== this.request) return
        this.groups = more ? this.groups.concat(result.groups) : result.groups
        this.count = result.count
        this.clientCount = result.clientCount
        this.loaded = true
      } catch (err) {
        if (request === this.request) this.showNotification({ text: err.message, color: 'red' })
      } finally {
        if (request === this.request) this.loading = false
      }
    },
    reload () {
      return Promise.all([this.loadSummary(), this.load(), this.loadLog()])
    },
    pick (id, type) {
      const pending = { ...this.pending }
      if (type) pending[id] = type
      else delete pending[id]
      this.pending = pending
    },
    // Everything picked, in one batch; then the list again, without them.
    async save () {
      const changes = Object.entries(this.pending).map(([id, paymentType]) => ({ id: Number(id), paymentType }))
      this.saving = true
      try {
        const result = await api.settleUnpaid({ changes })
        this.pending = {}
        this.showNotification({
          text: 'Salvestatud: ' + result.count + (result.count === 1 ? ' töö' : ' tööd') + (result.skipped ? ' (' + result.skipped + ' oli vahepeal juba muudetud)' : ''),
          color: 'success'
        })
        await this.reload()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = false
      }
    },
    askRule (rule, type) {
      this.asking = { rule: rule.key, before: rule.key === 'before' ? this.before : undefined, type, count: rule.count, what: rule.what }
    },
    async settle () {
      const { rule, before, type } = this.asking
      this.saving = true
      try {
        const result = await api.settleUnpaid({ rule, before, paymentType: type.value })
        this.asking = null
        this.showNotification({
          text: result.count + ' → ' + type.text + (result.skipped ? ' (' + result.skipped + ' oli vahepeal juba muudetud)' : ''),
          color: 'success'
        })
        await this.reload()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = false
      }
    },
    async undo () {
      this.saving = true
      try {
        const result = await api.undoUnpaid(this.undoing.id)
        this.undoing = null
        this.showNotification({ text: 'Tagasi võetud: ' + result.count + ' / ' + result.total, color: 'success' })
        await this.reload()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = false
      }
    },
    openEvent (entry) {
      this.eventId = entry.id
      this.eventChanged = false
      this.eventOpen = true
    },
    closeEvent () {
      this.eventOpen = false
      if (this.eventChanged) this.reload()
    },
    typeText (value) {
      const type = TYPES.find(type => type.value === value)
      return type ? type.text : 'Maksmata'
    },
    // "Tasuta", or "2 Arvega, 1 Tasuta" for a save that gave out several.
    batchTypes (batch) {
      const parts = Object.entries(batch.types || {})
      return parts.length > 1 ? parts.map(([type, n]) => n + ' ' + this.typeText(Number(type))).join(', ') : this.typeText(batch.paymentType)
    },
    ruleText (rule) {
      if (rule.startsWith('before:')) return 'enne ' + this.day(rule.slice(7))
      return { ids: 'käsitsi', guarantee: 'Garantii', contract: 'Lepinguline', aktInvoice: 'aktil on arve' }[rule] || rule
    },
    day (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : ''
    },
    dateTime (date) {
      return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : ''
    }
  },
  mounted () {
    this.reload()
    // Everyone, the deactivated too: old work was done by people who have left.
    api.getTechnics().then(({ users }) => {
      this.technics = users.slice().sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'et'))
    }).catch(() => {})
  },
  beforeUnmount () {
    clearTimeout(this.searchTimer)
  }
}
</script>

<style scoped>
.unpaid { padding-bottom: 96px; }
.unpaid__num { font-variant-numeric: tabular-nums; }
.unpaid__rule { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; padding: 10px 0; border-top: 1px solid rgba(0, 0, 0, .08); }
.unpaid__rule-text { flex: 1 1 260px; min-width: 0; }
.unpaid__rule-do { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
/* Stays under the app bar while the list scrolls; a card clips, which
   would stop it sticking. */
.unpaid__work { overflow: visible !important; }
.unpaid__save { position: sticky; top: var(--v-layout-top, 0px); z-index: 2; display: flex; flex-wrap: wrap; align-items: center; gap: 8px 10px; padding: 8px 12px; background: #EEF2EF; border: 1px solid rgba(0, 0, 0, .12); border-radius: 8px 8px 0 0; }
.unpaid__save-state { flex: 1 1 200px; min-width: 0; font-size: 14px; }
.unpaid__list { border: 1px solid rgba(0, 0, 0, .12); border-top: 0; border-radius: 0 0 8px 8px; }
.unpaid__head, .unpaid__row { display: grid; grid-template-columns: 88px 100px minmax(0, 1.1fr) minmax(0, 1.7fr) 130px 132px; gap: 4px 12px; align-items: center; padding: 6px 12px; }
.unpaid__head { font-size: 11px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; opacity: .6; }
.unpaid__row { border-top: 1px solid rgba(0, 0, 0, .08); font-size: 14px; }
.unpaid__row:hover { background: rgba(0, 0, 0, .03); }
.unpaid__row--changed, .unpaid__row--changed:hover { background: #FFF6DC; box-shadow: inset 3px 0 0 #E0B94A; }
.unpaid__day { font: inherit; font-weight: 600; color: rgb(var(--v-theme-primary)); background: none; border: 0; padding: 0; text-align: left; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; justify-self: start; }
.unpaid__client { font-weight: 600; overflow-wrap: anywhere; }
.unpaid__main { min-width: 0; overflow-wrap: anywhere; }
.unpaid__note { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.unpaid__pay { width: 100%; font: inherit; font-weight: 600; color: inherit; background: #fff; border: 1px solid rgba(0, 0, 0, .25); border-radius: 6px; padding: 5px 8px; }
.unpaid__row--changed .unpaid__pay { border-color: #E0B94A; }
/* A phone: date, type and technician on one line, the rest under it. */
@media (max-width: 760px) {
  .unpaid__head { display: none; }
  .unpaid__row { grid-template-columns: 1fr 1fr; padding: 10px 12px; }
  .unpaid__client, .unpaid__main, .unpaid__pay { grid-column: 1 / -1; }
  .unpaid__tech { grid-row: 1; grid-column: 2; text-align: right; }
  .unpaid__type { grid-row: 2; grid-column: 1 / -1; }
}
</style>
